<template>
  <div class="variant-picker">
    <div class="picker-header">
      <span class="text-sm font-semibold text-slate-700">Variante wählen</span>
      <button class="picker-close" type="button" @click="emit('close')">
        <Icon icon="fa6-solid:xmark"/>
      </button>
    </div>

    <div class="picker-grid">
      <button
          v-for="variant in dedupedVariants"
          :key="variant.variant_name"
          type="button"
          class="picker-option"
          :class="{
            'picker-option--current': variant.content_hash === currentHash,
            'picker-option--unplayable': !variant.playable,
          }"
          @click="emit('select', variant.variant_name)"
      >
        <div class="picker-option-head">
          <span :class="variant.direction === 'pull' ? 'text-blue-600' : 'text-slate-900'">
            {{ variant.label }}
          </span>
          <span class="picker-score">{{ Math.round(variant.score) }}</span>
          <Icon v-if="!variant.playable" icon="fa6-solid:triangle-exclamation" class="text-red-500"/>
          <Icon v-if="variant.content_hash === currentHash" icon="fa6-solid:check" class="text-green-600"/>
        </div>
        <OsmdMeasure :music-xml="variant.music_xml" :zoom="0.25"/>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {Icon} from '@iconify/vue';
import type {MeasureVariant} from '@/generated';
import OsmdMeasure from './OsmdMeasure.vue';

const {variants, current} = defineProps<{
  variants: MeasureVariant[];
  current: string;
}>();

const emit = defineEmits<{
  (e: 'select', variantName: string): void;
  (e: 'close'): void;
}>();

// Hash of the currently selected variant, so we can highlight the (possibly
// deduped) representative that is physically identical to the current choice.
const currentHash = computed(() => variants.find((v) => v.variant_name === current)?.content_hash ?? '');

// Collapse physically identical variants (same buttons) to a single representative,
// keeping the best (lowest) score, sorted best-first.
const dedupedVariants = computed<MeasureVariant[]>((oldValue) => {
  const byHash = new Map<string, MeasureVariant>();
  for (const variant of variants) {
    const existing = byHash.get(variant.content_hash);
    if (!existing || variant.score < existing.score) {
      byHash.set(variant.content_hash, variant);
    }
  }
  const result = [...byHash.values()].sort((a, b) => a.score - b.score);

  if (
      oldValue &&
      oldValue.length === result.length &&
      oldValue.every((o, i) => o.variant_name === result[i]?.variant_name)
  ) {
    return oldValue;
  }
  return result;
});
</script>

<style scoped>
@import 'tailwindcss';

.variant-picker {
  @apply absolute left-1/2 top-full z-30 mt-1 w-[22rem] max-w-[90vw] -translate-x-1/2
  rounded-xl border border-orange-400 bg-orange-100 p-3 shadow-xl;
}

.picker-header {
  @apply mb-2 flex items-center justify-between;
}

.picker-close {
  @apply rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700;
}

.picker-grid {
  @apply grid max-h-[800px] grid-cols-1 gap-0 overflow-y-auto bg-gray-100/50;
}

.picker-option {
  @apply flex flex-col max-h-[300px] gap-0 rounded-lg border border-slate-200 bg-slate-50 p-2 text-left
  transition-all hover:border-blue-400 hover:bg-blue-50 scale-80;

}

.picker-option--current {
  @apply border-green-400 hover:border-blue-400 bg-green-50/50;
}

.picker-option--unplayable {
  @apply border-red-200;
}

.picker-option-head {
  @apply flex items-center gap-1 text-xs font-semibold;
}

.picker-score {
  @apply ml-auto rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600;
}
</style>
